import {XText} from "../../components/xui";
import xcolors from "../../components/xui/xcolors";
import {searchActions, needsForceOrDisplay} from "./useSearch";
import {api} from "../../lib/api";
import {searchCategoriesByKey} from "./search-categories";
import {by} from "../../lib/sort";
import routes from "../../routes";
import {useToggle, useClickOutside, cx, WithTooltip, TooltipForChild} from "@cdx/common";
import {searchOverlayStyles as styles} from "./search.css";
import {getMyProjects, getRawSelectedProjects} from "../../lib/hooks/useSelectedProjects";
import {pluralize} from "../../lib/utils";
import {
  Box,
  Col,
  DSButton,
  DSIconArchive,
  DSIconArrowCurvedUp,
  DSIconButton,
  DSIconChevronRight,
  DSIconInfo,
  DSIconPlus,
  DSIconSearch,
  DSIconTrash,
  Row,
  DSTag,
  Text,
  css,
} from "@cdx/ds";
import {iconStyles} from "@cdx/ds/components/DSIcon/DSIcon.css";
import {filtersAreEqual} from "../../lib/search-utils";

const GoToOption = ({value, renderSuggestion, root, isSelected, onClick}) => (
  <button
    className={cx(styles.goToOption.default, isSelected && styles.goToOption.selected)}
    onClick={onClick}
  >
    {renderSuggestion(value, {root, isSelected})}
    <Col absolute top="0" bottom="0" right="0" px="4px" justify="center">
      <Col
        colorTheme="white"
        bg="foreground"
        rounded={4}
        className={styles.enterBox}
        opacity={isSelected ? 1 : 0}
      >
        <DSIconChevronRight
          size={16}
          className={iconStyles.setSize["12px"]}
          style={{color: xcolors.purple500}}
        />
      </Col>
    </Col>
  </button>
);
const SuggestionOption = ({value, renderSuggestion, root, isSelected, onClick}) => (
  <Row
    as="button"
    sp="8px"
    px="8px"
    py="4px"
    className={cx(styles.option.default, isSelected && styles.option.selected)}
    align="center"
    onClick={onClick}
  >
    {renderSuggestion(value, {root, isSelected})}
    <Col
      colorTheme="active50"
      bg="foreground"
      rounded={4}
      className={styles.enterBox}
      opacity={isSelected ? 1 : 0}
      ml="auto"
    >
      <DSIconSearch
        size={16}
        className={iconStyles.setSize["12px"]}
        style={{color: xcolors.active}}
      />
    </Col>
  </Row>
);

export const SuggestionBlock = ({suggestion, root, offset, selectedIndex, searchInputActions}) => {
  const {isFilter, entries, catLabel, renderSuggestion, trigger} = suggestion;
  const OptionComp = isFilter ? SuggestionOption : GoToOption;
  return (
    <Col sp="4px" className={isFilter ? styles.filterCatContainer : styles.nonFilterCatContainer}>
      <Row align="baseline">
        <XText preset="label" size={0} color={isFilter ? "gray600" : "purple500"}>
          {catLabel}
        </XText>
        {trigger && (
          <>
            <XText preset="bold" size="0" color="gray500" className={css({ml: "auto"})}>
              {trigger.value}
            </XText>
          </>
        )}
      </Row>
      {entries.length > 0 ? (
        <Col sp="4px">
          {entries.map((entry, i) => (
            <OptionComp
              key={entry.key}
              isSelected={offset + i === selectedIndex}
              root={root}
              renderSuggestion={renderSuggestion}
              value={entry.value}
              onClick={() => searchInputActions.selectOption({suggestion, entry})}
            />
          ))}
        </Col>
      ) : (
        <XText size={1} color="gray500">
          {trigger.emptyHelp}
        </XText>
      )}
    </Col>
  );
};

/**
 *
 * @param {import("../../cdx-models/User").User} loggedInUser
 * @returns {import("./search-types").SearchFilter[]}
 */
export const getDefaultSavedSearchFilter = (loggedInUser) => [
  {category: "cardOwner", value: loggedInUser.id},
];

export const getSearchLabel = (tokens, opts) => {
  const labels = tokens.map(({category, value}) => {
    const info = searchCategoriesByKey[category];
    return info ? info.savedSearchLabel(value, opts) : category;
  });
  if (labels.length === 1) labels.push({prio: 0, label: "cards"});
  labels.sort(by((l) => l.prio)).reverse();
  return labels.map((l) => l.label).join(" ");
};

const SavedSearchButton = ({filters, root, id, forceOr, active}) => (
  <Row sp="4px" align="center">
    <DSTag
      onClick={() => searchActions.setFilters(filters, {forceOr})}
      theme={active ? "active" : null}
    >
      {getSearchLabel(filters, {root})}
    </DSTag>
    {id && (
      <DSIconButton
        variant="tertiary"
        size="sm"
        onClick={() => api.mutate.search.delete({id})}
        negatePadding
        icon={<DSIconTrash />}
      />
    )}
  </Row>
);

const SavedSearches = ({root, filters, forceOr, hasExternalMyCardsButton}) => {
  const {loggedInUser} = root;
  const savedSearches = root.account.$meta.isLoaded
    ? loggedInUser.$meta.find("savedSearches", {accountId: root.account.id})
    : [];
  const defaultSearch = getDefaultSavedSearchFilter(root.loggedInUser);
  const isAlreadySavedEl =
    !filters.length ||
    [{tokens: defaultSearch, forceOr: false}, ...savedSearches].find(
      (s) => filtersAreEqual(s.tokens, filters) && s.forceOr === forceOr
    );

  const onSave = () =>
    api.mutate.search.save({
      ownerId: loggedInUser.id,
      tokens: filters,
      forceOr,
    });

  return (
    <Col sp="8px" align="start" flex="none">
      <Row sp="12px" align="start">
        {savedSearches.length === 0 && hasExternalMyCardsButton ? (
          <XText preset="bold" size={1} color="gray500">
            No Saved Searches
          </XText>
        ) : (
          <XText preset="bold" size={1} color="gray500">
            Saved Searches
          </XText>
        )}
        {!isAlreadySavedEl && (
          <DSIconButton
            icon={<DSIconPlus />}
            label="Save"
            size="sm"
            variant="tertiary"
            negatePadding
            onClick={onSave}
          />
        )}
      </Row>
      <Row sp="4px" wrap>
        {!hasExternalMyCardsButton && (
          <SavedSearchButton
            filters={defaultSearch}
            root={root}
            forceOr={false}
            active={isAlreadySavedEl?.tokens === defaultSearch}
          />
        )}
        {savedSearches.map((search) => (
          <SavedSearchButton
            key={search.id}
            filters={search.tokens}
            forceOr={search.forceOr}
            root={root}
            id={search.id}
            active={isAlreadySavedEl === search}
          />
        ))}
      </Row>
    </Col>
  );
};

const categories = [
  {label: "Effort", desc: `Type a number or "effort"`},
  {label: "Priority", desc: `Type a priority name (e.g. "medium") or "priority"`},
  {label: "Workflow State", desc: `Type a state name (e.g. "done") or "state" or "status"`},
  {label: "Project Tag", desc: `type '#' or the first three letters of the project tag`},
  {label: "Personal Tag", desc: `type '#' or the first three letters of your tag`},
  {
    label: "Other Tag",
    desc: `(i.e. personal tags from others) type '#' or the first three letters of that tag`,
  },
  {label: "Owner", desc: `type '@' or the first three letters of the user name`, nonPublic: true},
  {
    label: "Mentioned",
    desc: `type '@' or the first three letters of the user name`,
    nonPublic: true,
  },
  {
    label: "Created by",
    desc: `type '@' or the first three letters of the user name`,
    nonPublic: true,
  },
  {label: "Content (includes title)", desc: `type anything`},
  {label: "Title", desc: `use t: as prefix`},
  {
    label: "Run",
    desc: `Type "run" or the first two letters of a Run name`,
    nonPublic: true,
  },
  {
    label: "Run Configuration",
    desc: `Type "configuration" or the first two letters of a Run Configuration short name`,
    nonPublic: true,
  },
  {
    label: "Milestone",
    desc: `Type "milestone" or the first two letters of a milestone name`,
    nonPublic: true,
  },
  {label: "Deck", desc: `Type the first two letters of a deck name`},
  {label: "Project", desc: `Type the first two letters of a project name`},
  {label: "Hero or sub card", desc: `Type either "hero" or "sub"`},
  {label: "Locked or unlocked cards", desc: `Type "locked"`},
  {label: "Upvotes", desc: `Type "upvotes"`},
  {
    label: "Go to deck",
    desc: `Enter the deck name. You can also force this option by starting your query with 'gd:'. Use 'gad:' to find card in all projects no matter the project visibility.`,
  },
  {
    label: "Go to card",
    desc: `Enter the card title. You can also force this option by starting your query with 'gc:'. Use 'gac:' to find card in all projects no matter the project visibility.`,
  },
  {
    label: "Go to card by id",
    desc: `Enter the id including the '$' prefix.`,
  },
];

const Hint = () => {
  const [on, {toggle}] = useToggle();
  return (
    <Row
      sp="8px"
      align="start"
      colorTheme="purple25"
      bg="foreground"
      rounded={4}
      border="1px"
      px="8px"
      py="4px"
      flex="auto"
    >
      {on ? (
        <Col sp="8px">
          {categories
            .filter(({nonPublic}) => process.env.REACT_APP_MODE !== "open" || !nonPublic)
            .map(({label, desc}) => (
              <Col key={label}>
                <Text type="label12" color="primary">
                  {label}
                </Text>
                <Text type="prose12" color="secondary">
                  {desc}
                </Text>
              </Col>
            ))}
        </Col>
      ) : (
        <Text type="prose12" color="secondary">
          Type e.g. <span className={css({weight: "bold", color: "primary"})}>unassigned</span> or{" "}
          <span className={css({weight: "bold", color: "primary"})}>#tagname</span>
          {process.env.REACT_APP_MODE !== "open" && (
            <>
              {" "}
              or <span className={css({weight: "bold", color: "primary"})}>@user</span>
            </>
          )}{" "}
          to filter.
        </Text>
      )}
      <Box ml="auto">
        <TooltipForChild tooltip="Click to see full list of filters">
          <DSIconButton
            icon={<DSIconInfo />}
            size="sm"
            variant="tertiary"
            negatePadding
            active={on}
            onClick={toggle}
          />
        </TooltipForChild>
      </Box>
    </Row>
  );
};

export const SearchHint = Hint;

const Options = ({hasExternalArchiveButton, searchState, basedOnFullLibrary, shownFilters}) => {
  const options = [];
  const needsForceOr = needsForceOrDisplay(shownFilters);
  if (needsForceOr || searchState.forceOr) {
    options.push(
      <TooltipForChild
        tooltip={`Perform "or" search for multiple tag or content based queries`}
        key="or"
      >
        <DSButton
          size="sm"
          variant="tertiary"
          negatePadding
          active={searchState.forceOr}
          onClick={() => searchActions.toggleForceOr()}
        >
          OR
        </DSButton>
      </TooltipForChild>
    );
  }
  if (!hasExternalArchiveButton) {
    options.push(
      <TooltipForChild tooltip="Include archived cards" key="a">
        <DSIconButton
          icon={<DSIconArchive />}
          size="sm"
          variant="tertiary"
          negatePadding
          active={searchState.includeArchivedCards}
          onClick={() => searchActions.toggleArchived()}
        />
      </TooltipForChild>
    );
  }
  if (!basedOnFullLibrary && shownFilters.length > 0 && process.env.REACT_APP_MODE !== "open") {
    options.push(
      <TooltipForChild tooltip="Search in all decks" key="d">
        <DSIconButton
          icon={<DSIconArrowCurvedUp />}
          size="sm"
          variant="tertiary"
          negatePadding
          to={{pathname: routes.decks.getUrl(), state: {searchTokens: shownFilters}}}
        />
      </TooltipForChild>
    );
  }
  if (!options.length) return null;
  return (
    <Row ml="auto" sp="4px" align="center">
      {options}
    </Row>
  );
};

const Warning = ({root}) => {
  const selProjects = getRawSelectedProjects(root);
  if (selProjects.length === 0) return null;
  return (
    <WithTooltip
      tooltip={() =>
        `Your project selection excludes ${pluralize(
          getMyProjects(root).length - selProjects.length,
          "project"
        )}`
      }
      as={XText}
      size={0}
      color="gray500"
    >
      in <b>{selProjects.length}</b> shown projects
    </WithTooltip>
  );
};

const SearchOverlay = ({
  waitingForMore,
  cardCount,
  searchWithinLabel,
  notAffectedByProjectSelection,
  suggestions,
  selectedIndex,
  root,
  searchInputActions,
  searchState,
  hasExternalArchiveButton,
  hasExternalMyCardsButton,
  basedOnFullLibrary,
  onClose,
  shownFilters,
}) => {
  let offset = 0;
  const handlers = useClickOutside(onClose);

  return (
    <Col
      className={styles.container}
      px="12px"
      py="8px"
      sp="16px"
      colorTheme="white"
      data-cdx-context="SearchOverlay"
      bg="foreground"
      {...handlers}
    >
      {suggestions.length === 0 && (
        <Row sp="8px" flex="none">
          <Row sp="4px" align="baseline">
            <Text type="label12light" color="secondary">
              Searches{" "}
              <Text as="span" color="primary" type="label12">
                {pluralize(cardCount, "Card")}
              </Text>
              {searchWithinLabel && ` ${searchWithinLabel}`}
            </Text>
            {!notAffectedByProjectSelection && <Warning root={root} />}
          </Row>

          <Options
            hasExternalArchiveButton={hasExternalArchiveButton}
            searchState={searchState}
            basedOnFullLibrary={basedOnFullLibrary}
            shownFilters={shownFilters}
          />
        </Row>
      )}
      {suggestions.length === 0 && shownFilters.length === 0 && <Hint />}

      {!waitingForMore && suggestions.length > 0 && (
        <Col sp="12px" flex="none">
          {suggestions.map((suggestion) => {
            const prevOffset = offset;
            offset += suggestion.entries.length;
            return (
              <SuggestionBlock
                key={suggestion.category}
                selectedIndex={selectedIndex}
                root={root}
                suggestion={suggestion}
                offset={prevOffset}
                searchInputActions={searchInputActions}
              />
            );
          })}
        </Col>
      )}
      {process.env.REACT_APP_MODE !== "open" && suggestions.length === 0 && (
        <SavedSearches
          root={root}
          filters={shownFilters}
          forceOr={searchState.forceOr}
          hasExternalMyCardsButton={hasExternalMyCardsButton}
        />
      )}
    </Col>
  );
};

export default SearchOverlay;
